import { apiCall, type ApiResponse } from './apiClient';
import { fetchWithChatFeedback } from './chatFeedbackRequired';
import { toUserFacingError } from '../utils/userFacingError';
import type { WorkflowIntakePrefill } from '../components/HomeChat/WorkflowIntakePrefillNotice';
import type { OtherTransactionsStageKey, OtEntryOrigin } from '../constants/otherTransactionsStages';
export interface OtVendorClaim { id: string; fact_key: string; value: any; status: string; source_ids: string[]; source_url?: string; source_date?: string; retrieved_at?: string; excerpt?: string; provenance?: string; }
export interface OtVendor { id: string; name: string; organization_type?: string; website?: string; uei?: string; capability_note?: string; claims: OtVendorClaim[]; version: number; origin?: string; response_source_ids?: string[]; response_rfs_version?: number | null; }
export interface OtTeam { counterparty_vendor_id?: string | null; choice_rationale?: string; members: Array<{vendor_id: string; role: string}>; version: number; }
export interface OtQuestion { id: string; question: string; why?: string; essential?: boolean; vendor_ids: string[]; fact_key: string; source_ids?: string[]; }
export interface OtQuestionSet { id: string; version: number; questions: OtQuestion[]; input_fingerprint?: string; }
export interface OtAnswer { question_id: string; question_set_id: string; text: string; status: string; source_ids: string[]; vendor_ids: string[]; version: number; }
export interface OtDiscoveryRun { id: string; created_at?: string; sources?: Array<{source: string; status: string; message?: string; vendor_count?: number}>; source_statuses?: Record<string,{status:string;message?:string}>; status?: string; }
export interface OtArtifact {
    content: string;
    status: 'success' | 'suitable' | 'unsuitable' | 'needs_information' | 'supported' | 'unsupported';
    questions?: Array<{id: string; question: string; why?: string; essential?: boolean}>;
    suggestions?: string[];
    source_ids: string[];
    missing_facts: string[];
    outstanding_actions: string[];
    version: number;
    created_at: string;
    workflow_approved: boolean;
    findings?: {vendor_bases?: Record<string,{basis:string;response_source_ids:string[];rfs_version?:number}>; [key:string]:any};
}
export interface OtStage {
    inputs: Record<string, any>;
    artifact: OtArtifact | null;
    history: OtArtifact[];
    stale: boolean;
    error?: string;
}
export interface OtEvidence {
    id?: string;
    evidence_id?: string;
    label: string;
    filename?: string;
    url?: string;
    kind?: string;
    marking?: string;
    extraction_status?: string;
    extraction_notice?: string;
    summary?: string;
    text_available?: boolean;
    role?: string;
}
export interface OtherTransactionsWorkspace {
    schema_version: number;
    flow_id: string;
    revision: number;
    entry_origin: OtEntryOrigin;
    route_policy: string;
    route: Array<{key: OtherTransactionsStageKey; id: number; name: string; purpose: string}>;
    project_context: Record<string, any>;
    intake_prefill?: WorkflowIntakePrefill | null;
    context_provenance?: Record<string, {source: string; revision: number; status: string}>;
    context_history?: Array<any>;
    stages: Record<OtherTransactionsStageKey, OtStage>;
    evidence: OtEvidence[];
    completed_stages: OtherTransactionsStageKey[];
    completed_count: number;
    active_stage: OtherTransactionsStageKey;
    stopped: boolean;
    package_ready: boolean;
    package_version: number;
    remaining_actions: string[];
    read_only?: boolean;
    vendors: OtVendor[];
    shortlist: {vendor_ids: string[]; version: number};
    proposed_team: OtTeam;
    discovery_runs: OtDiscoveryRun[];
    question_sets: Partial<Record<'vendor_assessment' | 'performer_eligibility', OtQuestionSet>>;
    answers: OtAnswer[];
}
const base = (flowId: string) => `/api/other-transactions/${encodeURIComponent(flowId)}`;
export const getOtherTransactionsWorkspace = (flowId: string) => apiCall<OtherTransactionsWorkspace>(base(flowId), 'GET');
export const saveOtherTransactionsDraft = (flowId: string, key: OtherTransactionsStageKey, expectedRevision: number, inputs: Record<string, any>, content?: string, projectContext?: Record<string, any>) => apiCall<OtherTransactionsWorkspace>(`${base(flowId)}/stages/${key}/draft`, 'PATCH', { expected_revision: expectedRevision, inputs, ...(content !== undefined ? { content } : {}), ...(projectContext !== undefined ? { project_context: projectContext } : {}) }, { retries: 0 });
export const generateOtherTransactionsStage = (flowId: string, key: OtherTransactionsStageKey, expectedRevision: number, useSavedWork: boolean) => apiCall<{
    workspace: OtherTransactionsWorkspace;
    reused: boolean;
}>(`${base(flowId)}/stages/${key}/generate`, 'POST', { expected_revision: expectedRevision, use_saved_work: useSavedWork }, { retries: 0 });
/** Explicit OT operations never invoke the Services stage-606 boundary. */
const operation = (flowId: string, key: OtherTransactionsStageKey, name: string, expectedRevision: number, useSavedWork: boolean) => apiCall<{workspace: OtherTransactionsWorkspace; reused: boolean}>(`${base(flowId)}/stages/${key}/${name}`, 'POST', {expected_revision: expectedRevision, use_saved_work: useSavedWork}, {retries: 0});
export const searchOtherTransactionsVendors = (flowId: string, expectedRevision: number, useSavedWork: boolean) => operation(flowId, 'vendor_discovery', 'search', expectedRevision, useSavedWork);
export const suggestOtherTransactionsCodes = (flowId: string, expectedRevision: number, useSavedWork: boolean) => operation(flowId, 'vendor_discovery', 'suggest-codes', expectedRevision, useSavedWork);
export const generateOtherTransactionsQuestions = (flowId: string, key: 'vendor_assessment' | 'performer_eligibility', expectedRevision: number, useSavedWork: boolean) => operation(flowId, key, 'questions', expectedRevision, useSavedWork);
export const assembleOtherTransactionsPackage = (flowId: string, expectedRevision: number) => apiCall<{
    workspace: OtherTransactionsWorkspace;
    reused: boolean;
}>(`${base(flowId)}/assemble`, 'POST', { expected_revision: expectedRevision }, { retries: 0 });
export const addOtherTransactionsReference = (flowId: string, expectedRevision: number, label: string, url: string, summary: string, marking: string, role = 'supporting') => apiCall<OtherTransactionsWorkspace>(`${base(flowId)}/references`, 'POST', { expected_revision: expectedRevision, label, url, summary, marking, role }, { retries: 0 });
export async function uploadOtherTransactionsEvidence(flowId: string, file: File, label: string, marking: string, role = 'supporting'): Promise<ApiResponse<OtherTransactionsWorkspace>> {
    const body = new FormData();
    body.append('file', file);
    body.append('label', label || file.name);
    body.append('marking', marking);
    body.append('role', role);
    try {
        const response = await fetchWithChatFeedback(`${process.env.REACT_APP_API_URL || ''}${base(flowId)}/evidence`, { method: 'POST', body, credentials: 'include' });
        const result = await response.json();
        if (response.status === 401)
            window.dispatchEvent(new Event('auth-expired'));
        return result;
    }
    catch (error) {
        return { success: false, error: toUserFacingError(error) };
    }
}
export async function downloadOtherTransactionsFile(flowId: string, path: string, filename: string): Promise<void> {
    const response = await fetchWithChatFeedback(`${process.env.REACT_APP_API_URL || ''}${base(flowId)}/${path}`, { credentials: 'include' });
    if (!response.ok) {
        if (response.status === 401)
            window.dispatchEvent(new Event('auth-expired'));
        throw new Error('The download could not be prepared. Please try again.');
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}
